import type { CatalogPiece, CatalogPieceDetail } from "@/lib/api/api-client";
import { GESTURE_TIERS, gestureTier, workSize, type WorkSize } from "@/lib/library/difficulty-tiers";
import { formatDuration, parseCatalogTonality, pieceDisplayArtist } from "@/lib/library/piece-format";
import { isBareTitle, searchableTitle, titlePhrase } from "@/lib/library/piece-title-phrase";
import { tonalityLabel } from "@/lib/music/tonality";

/**
 * La description d'une fiche de morceau — celle que Google affiche sous le
 * titre, et souvent le seul texte que quelqu'un lit avant de cliquer.
 *
 * # Elle ouvre sur ce que la personne a tapé
 *
 * « Une main pose, l'autre suit. Sol majeur, 88 BPM. 21 mesures, 0:56… » ne
 * répond à personne : ni le nom du morceau, ni le mot « piano », ni ce qu'on
 * vient y faire. Google le voyait bien et réécrivait l'extrait à partir du
 * tableau de la fiche, ce qui donnait « Si mineur. Tonalité ; 100 BPM. Tempo ».
 *
 * La première phrase dit donc l'intention, le titre dedans : « Apprends le Lac
 * des cygnes au piano, sans partition. » C'est la requête presque mot pour mot,
 * et Google met en gras ce qui correspond. Faire entrer un titre dans une
 * phrase française demande un article et une coupe : `piece-title-phrase.ts`.
 *
 * # Puis ce qui distingue ce morceau des autres
 *
 * Une phrase unique à trous donnerait 211 pages jumelles : Google en indexe une
 * et laisse les autres de côté. Le titre en tête suffit à les distinguer, mais
 * il n'apprend rien de plus que le titre.
 *
 * Ce qui suit est donc classé par **saillance** : chaque phrase possible porte
 * une priorité inverse de sa fréquence dans le catalogue, et on retient la plus
 * rare qui s'applique. Sur les 210 pièces publiées, le trait le plus fréquent
 * couvre 19 % du catalogue au lieu de 100 %.
 *
 * On ne dit rien des bandes banales : les deux tiers du catalogue tiennent
 * entre 3 % et 32 % de touches noires, l'annoncer n'apprend rien.
 *
 * # Ce que ces champs mesurent, et ce qu'ils ne mesurent pas
 *
 * `handSpanMax` est l'écartement maximal **par position**, et 12 n'est pas un
 * seuil choisi ici : c'est le `handFrame` de l'analyseur, l'octave que couvre
 * une main sans se déplacer. Au-delà, l'analyseur dit lui-même qu'il faut
 * décaler, sauter ou passer le pouce — la main **se déplace**, elle ne s'ouvre
 * pas. Écrire « ouvre la main jusqu'à la dizième » sur un morceau à 39 serait
 * faux, et quelqu'un qui essaie de s'étirer pour y arriver se fait mal.
 *
 * `rhythmValues` compte des durées distinctes, pas de la syncope : on écrit le
 * compte, jamais « rythme simple ». `blackKeyShare` est une quantité, pas une
 * difficulté — la main est souvent mieux posée sur les noires.
 *
 * # Ce qui ne sort jamais
 *
 * Le chiffre de difficulté, sous aucune forme : le palier de geste et la taille
 * de chantier sont la seule expression publiable du niveau. Et le mot
 * « facile », qu'un adulte bloqué lit comme un verdict sur lui-même. Ce mot vit
 * sur `/library`, où il y a la place de le recadrer.
 */

/** Ce que Google affiche avant de couper. */
const MAX_LENGTH = 158;

/**
 * Les familles de traits. Une description ne prend jamais deux phrases de la
 * même famille : « 15 durées de note » suivi de « 5 durées de note » est
 * impossible, mais « au plus large, une octave et demie » suivi de « un tiers
 * de touches noires » dit deux choses.
 */
type ClaimFamily = "noires" | "empan" | "rythme" | "tempo" | "palier" | "tonalite";

type Claim = {
  family: ClaimFamily;
  text: string;
};

/**
 * Les traits saillants d'un morceau, du plus rare au plus commun.
 *
 * Les bandes de chaque famille sont disjointes, donc une famille contribue au
 * plus une phrase. Les bandes tues sont voulues : 7 à 9 durées de note (27 % du
 * catalogue) et 60 à 139 BPM (la majorité) ne distinguent rien.
 */
function salientClaims(piece: CatalogPiece): Claim[] {
  const claims: Claim[] = [];
  const black = piece.blackKeyShare;
  const span = piece.handSpanMax;
  const rhythm = piece.rhythmValues;
  const bpm = piece.bpm;

  const push = (family: ClaimFamily, text: string) => {
    claims.push({ family, text });
  };

  // 1-2 — ce qu'un débutant cherche littéralement.
  if (black === 0) {
    push("noires", "Pas une seule touche noire.");
  } else if (black !== null && black <= 2) {
    push("noires", "Presque aucune touche noire.");
  }

  // 3 — sous l'octave, la main tient sa position sans effort.
  if (span !== null && span <= 7) {
    push("empan", "Ta main ne s'ouvre jamais plus qu'une quinte.");
  } else if (span !== null && span <= 9) {
    push("empan", "Ta main ne s'ouvre jamais plus qu'une sixte.");
  }

  // 4 — si peu de durées que le rythme cesse d'être un sujet.
  if (rhythm !== null && rhythm <= 3) {
    push("rythme", `${rhythmCountWord(rhythm)} dans tout le morceau : le rythme s'efface.`);
  }

  if (black !== null && black >= 55) {
    push("noires", "Plus d'une note sur deux tombe sur une touche noire.");
  }

  if (span !== null && span >= 36) {
    push("empan", "Le passage le plus large couvre trois octaves : la main voyage.");
  }

  // « au tempo écrit » est vrai, rassurant, et décrit le produit : on monte par
  // paliers plutôt que de jouer vite tout de suite.
  if (bpm !== null && bpm < 60) {
    push("tempo", `À ${bpm} BPM, chaque note a le temps d'arriver.`);
  }

  if (rhythm === 4) {
    push("rythme", "Quatre durées de note en tout, pas une de plus.");
  }

  if (bpm !== null && bpm >= 160) {
    push("tempo", `${bpm} BPM au tempo écrit : tu commences moins vite.`);
  }

  if (black !== null && black >= 45 && black <= 54) {
    push("noires", "Une note sur deux, ou presque, tombe sur une touche noire.");
  }

  if (rhythm !== null && rhythm >= 15) {
    push("rythme", `${rhythm} durées de note différentes : le rythme demande autant que les doigts.`);
  }

  if (span !== null && span >= 24 && span <= 35) {
    push("empan", "Le passage le plus large couvre deux octaves : la main saute.");
  }

  if (span !== null && span >= 17 && span <= 23) {
    push("empan", "Au plus large, la main couvre une octave et demie.");
  }

  if (black !== null && black >= 33 && black <= 44) {
    push("noires", "Un tiers des notes tombent sur les touches noires.");
  }

  if (span !== null && span >= 13 && span <= 16) {
    push("empan", "Ta main dépasse l'octave par endroits : elle se replace.");
  }

  if (span !== null && span >= 10 && span <= 12) {
    push("empan", "Ta main ne s'ouvre jamais plus grand qu'une octave.");
  }

  if (rhythm !== null && rhythm >= 10 && rhythm <= 14) {
    push("rythme", `${rhythm} durées de note différentes : le rythme varie beaucoup.`);
  }

  if (rhythm !== null && rhythm >= 5 && rhythm <= 6) {
    push("rythme", `${rhythm} durées de note, pas une de plus.`);
  }

  if (bpm !== null && bpm >= 140 && bpm <= 159) {
    push("tempo", `${bpm} BPM au tempo écrit, et tu montes par paliers.`);
  }

  return claims;
}

function rhythmCountWord(rhythm: number): string {
  if (rhythm <= 1) {
    return "Une seule durée de note";
  }

  if (rhythm === 2) {
    return "Deux durées de note";
  }

  return "Trois durées de note";
}

/**
 * Le repli quand l'analyse de geste manque — c'est le cas de tout le catalogue
 * de production tant que `app:catalog:gesture-metrics --apply` n'y a pas tourné.
 * Le palier de geste et la tonalité redeviennent alors ce qu'il y a de plus
 * distinctif à dire.
 */
function fallbackClaims(detail: CatalogPieceDetail, measured: readonly Claim[]): Claim[] {
  const { piece } = detail;
  const claims: Claim[] = [];
  const tier = gestureTier(piece.difficulty, piece.durationSec);
  const hasFamily = (family: ClaimFamily) => measured.some((claim) => claim.family === family);

  // Le palier dit déjà ce que font les mains : redondant avec une phrase
  // d'empan, utile en son absence.
  if (tier !== null && !hasFamily("empan")) {
    claims.push({ family: "palier", text: `${GESTURE_TIERS[tier].long}.` });
  }

  const tonality = parseCatalogTonality(piece.tonality);

  if (tonality === null || piece.bpm === null || hasFamily("tempo")) {
    return claims;
  }

  const label = tonalityLabel(tonality, "latin");

  /*
    Un tiers du catalogue porte sa tonalité dans son titre — « Adagio du
    Concerto en ré mineur », « Toccata et fugue en ré mineur ». La répéter juste
    en dessous consomme trente caractères pour ne rien ajouter, et Google coupe
    à 158.
  */
  if (piece.title.toLowerCase().includes(label.toLowerCase())) {
    return claims;
  }

  claims.push({ family: "tonalite", text: `${label}, ${piece.bpm} BPM.` });

  return claims;
}

/**
 * Le chantier : combien il y a à faire, dans une unité de temps qui parle.
 *
 * Deux garde-fous contre la phrase absurde. Un morceau dense et court —
 * 965 notes en 1:02 — annoncé « un mois de travail » à côté de sa durée se lit
 * comme une contradiction ; on parle alors de densité. Et un morceau long mais
 * clairsemé annoncé « ce soir » a l'air d'une promesse en l'air.
 */
function workLine(piece: CatalogPiece, size: WorkSize | null): string | null {
  const duration = formatDuration(piece.durationSec);

  if (duration === null || piece.measureCount <= 0) {
    return null;
  }

  const head = `${piece.measureCount} mesures, ${duration}`;
  const seconds = piece.durationSec ?? 0;

  if ((size === "month" || size === "project") && seconds <= 120) {
    const notes = piece.noteCount;

    if (notes !== null) {
      return `${notes} notes en ${duration} : c'est dense.`;
    }
  }

  if (size === "evening" && seconds >= 180) {
    return `${head} : peu de notes, mais elles durent.`;
  }

  switch (size) {
    case "evening":
      return `${head} : tu peux aller au bout ce soir.`;
    case "week":
      return `${head} : compte une semaine.`;
    case "month":
      return `${head} : un mois de travail.`;
    case "project":
      return `${head} : un projet.`;
    default:
      return `${head}.`;
  }
}

function shortWorkLine(piece: CatalogPiece): string | null {
  const duration = formatDuration(piece.durationSec);

  if (duration === null || piece.measureCount <= 0) {
    return null;
  }

  return `${piece.measureCount} mesures, ${duration}.`;
}

/**
 * La phrase d'intention, celle qui ouvre : le verbe qu'on tape, le nom du
 * morceau, l'instrument, et ce qui distingue la méthode.
 *
 * « sans partition » plutôt qu'une formule de promesse : c'est le seul mot de
 * méthode qui soit vrai sur les 211 fiches, et c'est aussi ce que quelqu'un
 * cherche. Le repli sert les titres qui refusent la tournure — une apposition
 * qu'on n'a pas pu couper, un titre qui ouvre sur une préposition.
 */
function learnLine(piece: CatalogPiece): string | null {
  const name = searchableTitle(piece.title);

  if (name === "") {
    return null;
  }

  const phrase = titlePhrase(piece.title);

  if (phrase === null) {
    return `${name}, à apprendre au piano sans partition.`;
  }

  const subject = `${phrase}${signature(piece)}`;

  // « la Pièce pour piano au piano » : trois titres nomment déjà l'instrument,
  // et le mot y est aussi bien placé pour un moteur.
  if (/piano/i.test(name)) {
    return `Apprends ${subject}, sans partition.`;
  }

  return `Apprends ${subject} au piano, sans partition.`;
}

/**
 * Le compositeur, mais seulement quand le titre ne désigne rien sans lui.
 *
 * On ne le met pas partout : la balise `title` le porte déjà, et « de Piotr
 * Ilitch Tchaïkovski » coûterait vingt-cinq caractères sur cent cinquante-huit
 * pour répéter la ligne du dessus. Il ne rentre que sur les titres d'un seul
 * mot, où il est la seule chose qui distingue trois fiches.
 */
function signature(piece: CatalogPiece): string {
  const composer = piece.composer;

  if (composer === null || composer === "" || !isBareTitle(piece.title)) {
    return "";
  }

  // Le « h » reste hors de l'élision : on écrit « de Haendel », pas
  // « d'Haendel ».
  return /^[aàâeéèêiîoôuùy]/i.test(composer) ? ` d'${composer}` : ` de ${composer}`;
}

/**
 * La phrase écrite à la main, derrière l'intention.
 *
 * Elle bat toute règle : c'est la seule qui dise *pourquoi* ce morceau, et elle
 * a été pensée pour être lue. Elle n'existe que sur les cinq pièces des
 * sélections curées, c'est-à-dire les fiches les plus en avant du produit.
 *
 * Les deux entières ne tiennent presque jamais dans les 158 caractères : sur
 * les cinq, quatre débordent. Laisser la note gagner seule ramenait exactement
 * le défaut qu'on corrigeait, une description qui ne nomme pas le morceau et ne
 * dit pas « piano ». On garde donc l'intention et on ne prend que la première
 * phrase de la note, qui porte à elle seule la raison éditoriale — « Garde-le
 * pour quand tu auras besoin d'une récompense. »
 *
 * `null` quand rien ne rentre, y compris la note seule : l'appelant compose
 * alors la description par la règle.
 */
function shelfLine(opening: string, note: string): string | null {
  for (const text of [note, firstSentence(note)]) {
    if (text === null) {
      continue;
    }

    const candidate = join(opening, text);

    if (candidate.length <= MAX_LENGTH) {
      return candidate;
    }
  }

  return note.length <= MAX_LENGTH ? note : null;
}

/**
 * Fin de phrase : une ponctuation forte, une espace, une majuscule.
 *
 * La majuscule est ce qui distingue une vraie coupe d'une abréviation — « Op. 9
 * », « K. 431 » sont suivis d'un chiffre. Et on refuse les coupes trop
 * précoces, où il ne resterait rien à lire.
 */
const SENTENCE_END = /[.!?] (?=[A-ZÀÂÄÇÉÈÊËÎÏÔÖÙÛÜ])/;
const MIN_SENTENCE_LENGTH = 15;

function firstSentence(note: string): string | null {
  const end = SENTENCE_END.exec(note);

  if (end === null || end.index + 1 < MIN_SENTENCE_LENGTH) {
    return null;
  }

  return note.slice(0, end.index + 1);
}

function join(...parts: (string | null)[]): string {
  return parts.filter((part) => part !== null).join(" ");
}

export function pieceDescription(detail: CatalogPieceDetail): string {
  const { piece } = detail;
  const opening = learnLine(piece);

  // Un titre à rallonge mangerait le budget à lui seul, un titre vide ne dirait
  // rien : la fiche retombe alors sur la phrase de repli, dont la longueur ne
  // dépend que du compositeur.
  if (opening === null || opening.length > MAX_LENGTH) {
    return lastResort(piece);
  }

  if (detail.shelfNote !== null) {
    const curated = shelfLine(opening, detail.shelfNote);

    if (curated !== null) {
      return curated;
    }
  }

  const measured = salientClaims(piece);
  const claims = [...measured, ...fallbackClaims(detail, measured)];

  const size = workSize(piece.noteCount ?? detail.noteCount, piece.durationSec);
  const long = workLine(piece, size);
  const short = shortWorkLine(piece);

  /*
    On ajoute ensuite ce qui rentre, du plus disant au plus court : deux traits
    de familles différentes et le chantier, puis un trait et le chantier, puis
    le chantier seul, puis un trait seul. La phrase d'intention, elle, ne se
    perd jamais.

    Les deux boucles à un trait parcourent *tous* les traits, pas seulement le
    plus saillant : le plus rare est souvent le plus long, et le laisser gagner
    coûterait le chantier — « 15 durées de note différentes… » chasse « 965
    notes en 1:02 : c'est dense ». Un trait un peu moins rare qui laisse la
    place aux deux en dit davantage.
  */
  const opener = claims[0] ?? null;

  if (opener !== null) {
    for (const second of claims.slice(1)) {
      if (second.family === opener.family) {
        continue;
      }

      for (const work of [long, short]) {
        const candidate = join(opening, opener.text, second.text, work);

        if (candidate.length <= MAX_LENGTH) {
          return candidate;
        }
      }
    }
  }

  for (const claim of claims) {
    for (const work of [long, short]) {
      const candidate = join(opening, claim.text, work);

      if (candidate.length <= MAX_LENGTH) {
        return candidate;
      }
    }
  }

  for (const work of [long, short]) {
    const candidate = join(opening, work);

    if (candidate.length <= MAX_LENGTH) {
      return candidate;
    }
  }

  for (const claim of claims) {
    const candidate = join(opening, claim.text);

    if (candidate.length <= MAX_LENGTH) {
      return candidate;
    }
  }

  return opening;
}

/**
 * Le repli des titres impossibles : rien du morceau ne rentre, il reste qui
 * l'a écrit.
 */
function lastResort(piece: CatalogPiece): string {
  const artist = pieceDisplayArtist(piece);

  if (artist === "") {
    return `Un morceau du domaine public à jouer au piano, sans partition.`;
  }

  return `Un morceau de ${artist} à jouer au piano, sans partition.`;
}
